import * as ROUTES from 'constants/routes'
import { Transitions } from '@mui/material'
import CssBaseline from '@mui/material/CssBaseline'
import Grid from '@mui/material/Grid'
import { styled } from '@mui/system'
import { ReactQueryDevtools } from 'react-query/devtools'
import { Redirect, Route, useRouteMatch } from 'react-router-dom'
import useTracking from 'utils/useTracking'
import { useAuth } from 'services/auth'
import { isOnPrem } from 'config'
import HomePage from './Home'
import LoginPage from './LoginPage'
import CellBrowsingPage from './cell-browsing/CellBrowsingPage'
import { CellVisualizationDemoLandingPage } from './cell-visualizations/demo'
import useCellVisualizationUrlParams from './cell-visualizations/useCellVisualizationUrlParams'
import DemoRegister from './demo-register'
import RunsPage from './runs/RunPage'
import DeepcellFooter from './shared/DeepcellFooter'
import DeepcellHeader from './shared/DeepcellHeader'
import PrivateRoute from './shared/PrivateRoute'
import ContentLoading from './shared/ContentLoading'
import useFlags, { useLoadFlagsFromRemoteEffect } from './shared/useFlags'
import useFlagCondition from './shared/useFlagCondition'
import { CellVisSelection } from './cell-visualizations/sessionSelect'
import CellVisualizationsPage from './cell-visualizations'
import LabelingPage from './cell-visualizations/labeling/LabelingPage'
import QuickVetPage from './cell-visualizations/labeling/QuickVetPage'
import RunMetricsEstimator from './experiment-planning'
import AdminToolsPage from './admin-tools/AdminToolsPage'
import AboutPage from './AboutPage'

const Root = styled('div')({
  display: 'flex',
  height: '100%',
  overflow: 'hidden',
})

const NavigationHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  justifyContent: 'flex-end',
  minHeight: 48,
}))

const GridContent = styled(Grid)(({ theme }) => {
  const transitions = theme.transitions as Transitions
  return {
    flexGrow: 1,
    transition: transitions.create('margin', {
      easing: transitions.easing.sharp,
      duration: transitions.duration.leavingScreen,
    }),
  }
})

const CellVizOrRedirect = () => {
  const { demoEnabled } = useFlags()
  const { sessionId } = useCellVisualizationUrlParams()

  if (!sessionId) {
    if (demoEnabled === 'yes') return <Redirect to={{ pathname: ROUTES.DEMO_DATASETS }} />
    return <CellVisSelection />
  }
  return <CellVisualizationsPage />
}

// Guards the internal labeling route. Labeling is a Deepcell-internal workflow,
// so non-internal users who hit the URL directly are redirected home. Wait for
// auth/permissions to finish loading before deciding, so a genuinely internal
// user is never bounced during auth resolution.
const LabelingOrRedirect = () => {
  const { isInternalUser, isLoading } = useAuth()
  if (isLoading) return <ContentLoading />
  if (!isInternalUser) return <Redirect to={{ pathname: ROUTES.HOME }} />
  return <LabelingPage />
}

// Session-less "vet labels from a TSV" page, internal only (same gating pattern).
const VetLabelsOrRedirect = () => {
  const { isInternalUser, isLoading } = useAuth()
  if (isLoading) return <ContentLoading />
  if (!isInternalUser) return <Redirect to={{ pathname: ROUTES.HOME }} />
  return <QuickVetPage />
}

export default function NavigationMenu(): JSX.Element {
  // Enable analytics tracking here at the top level component below the <Router>
  useTracking()
  useLoadFlagsFromRemoteEffect()

  const { isInternalUser } = useAuth()
  const experimentPlanningEnabled = useFlagCondition('experimentPlanningEnabled')

  // Admin Tools is accessible to:
  // - Internal users in GCP environments
  // - All users in ON_PREM environments
  const adminToolsEnabled = isOnPrem || isInternalUser

  const isRegister = useRouteMatch(ROUTES.DEMO_REGISTER)
  const isDemoDatasets = useRouteMatch(ROUTES.DEMO_DATASETS)

  return (
    <Root>
      <CssBaseline />
      <>
        {(!isRegister || !isDemoDatasets) && <DeepcellHeader />}
        <GridContent>
          {!isRegister && !isDemoDatasets && <NavigationHeader />}
          <Route exact path={ROUTES.LOGIN} component={LoginPage} />
          <Route exact path={ROUTES.HOME} component={HomePage} />
          <Route exact path={ROUTES.DEMO_REGISTER} component={DemoRegister} />
          <PrivateRoute exact path={ROUTES.CELL_BROWSING} component={CellBrowsingPage} />
          <PrivateRoute exact path={ROUTES.RUNS} component={RunsPage} />
          {experimentPlanningEnabled && (
            <PrivateRoute exact path={ROUTES.EXPERIMENT_PLANNING} component={RunMetricsEstimator} />
          )}
          {adminToolsEnabled && (
            <PrivateRoute exact path={ROUTES.ADMIN_TOOLS} component={AdminToolsPage} />
          )}
          <PrivateRoute
            exact
            path={ROUTES.CELL_VISUALIZATIONS_SESSION_LABEL}
            component={LabelingOrRedirect}
          />
          <PrivateRoute exact path={ROUTES.LABEL_VET} component={VetLabelsOrRedirect} />
          <PrivateRoute
            exact
            path={ROUTES.ALL_CELL_VISUALIZATION_ROUTES}
            component={CellVizOrRedirect}
          />
          <PrivateRoute
            exact
            path={ROUTES.DEMO_DATASETS}
            component={CellVisualizationDemoLandingPage}
          />
          <PrivateRoute exact path={ROUTES.ABOUT} component={AboutPage} />
          <DeepcellFooter />
        </GridContent>
        <ReactQueryDevtools />
      </>
    </Root>
  )
}
